import { isPlatformBrowser } from '@angular/common';
import { inject, PLATFORM_ID } from '@angular/core';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { TranslateDefaultParser } from '@ngx-translate/core';

import { TRANSLATE_CONFIG } from './+state';
import { SharedTranslationsService } from './shared-translations.service';

/**
 * Custom parser for ngx-translate that tracks which translations have been requested by the current application
 */
export class SharedTranslateParser extends TranslateDefaultParser {
  private readonly requestedTranslations = new Set<string>();
  private readonly submitTranslationsDelay = 3 * 1000;
  private submitDebounceTimer: NodeJS.Timeout | null = null;

  private translationsService = inject(SharedTranslationsService);
  private log = inject(SharedLoggingService);
  private config = inject(TRANSLATE_CONFIG);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Submitting requested keys back to the API is browser-only, register-only telemetry. On the
  // SERVER it must never arm, because the debounced 3s setTimeout keeps the Node event loop alive
  // and STALLS each prerendered page ~3s before it can serialize — the landing prerender regression
  // (AM-5056): when the shared chrome started using `| lhTranslate` (AM-4866), getValue ran on every
  // prerendered page, arming this timer, so ~1 page in 4 paid a 3s wall (0.13s → 2.8s/page). It is
  // also pointless when `registerKeys === false` (e.g. landing is a read-only translation surface),
  // since submitRequestedTranslations() early-returns there anyway — so don't even schedule it.
  private readonly submitsKeys = this.isBrowser && this.config.registerKeys !== false;

  public override getValue(target: Record<string, string>, key: string): unknown {
    this.registerRequestedTranslation(key);
    return super.getValue(target, key);
  }

  private registerRequestedTranslation(key: string): void {
    if (!this.submitsKeys || !this.isTranslationKey(key) || this.requestedTranslations.has(key)) {
      // Not a submitting context (server / registerKeys:false), key already registered, or not a key.
      return;
    }
    this.requestedTranslations.add(key);

    if (this.submitDebounceTimer) {
      clearTimeout(this.submitDebounceTimer);
    }

    this.submitDebounceTimer = setTimeout(() => this.submitRequestedTranslations(), this.submitTranslationsDelay);
  }

  private submitRequestedTranslations(): void {
    this.submitDebounceTimer = null;

    const subscription = this.translationsService
      .registerAppTranslationKeys(Array.from(this.requestedTranslations))
      .subscribe({
        // HACK: Using a timeout to avoid referencing the subscription before it is ready
        complete: () => setTimeout(() => subscription.unsubscribe(), 1),
        error: (err) => void this.log.error('Failed to submit translations', err),
      });
  }

  private isTranslationKey(key: string): boolean {
    //
    // Translation keys by convention are uppercase and may contain underscores. If we encounter a key that does not
    // match this pattern, it is likely a hardcoded text string and not a translation key.
    //
    return /^[A-Z_0-9]+$/.test(key);
  }
}
